/**
 * Simulator - ヘッドレス対戦シミュレーター
 *
 * UIなしでゲームを実行し、AI vs AI対戦をシミュレートする。
 * トーナメント賭けシステムのオッズ計算に使用。
 *
 * 2025-11-30: 対戦AIストラテジー統合
 * - 難易度設定（easy/normal/hard）対応
 * - NPC別ストラテジー対応
 *
 * 2025-12-05: デバッグモード追加
 * - AI思考ログ出力機能
 * - 魔法カード使用判断の詳細ログ
 */

import {
  createInitialState,
  getPlayer,
  getCurrentPlayer,
  hasMonsterOnField,
  getFirstEmptySlot,
  canSummonCard,
  canMonsterAttack,
  PHASES,
  GAME_STATUS,
  type GameState,
  type PlayerState,
} from './GameState';

import {
  applyAction,
  actions,
  type GameAction,
} from './GameActions';

import { fireTriggerPure } from './triggerEnginePure';
import { TRIGGER_TYPES } from '../triggerTypes';
import { getStrategy, baseStrategy } from '../ai/strategies';
import type { AIStrategy, AIGameState, UsableSkill, ChargeableMonster } from '../ai/strategies';
import { createPureContext } from './contextAdapter';
import { getCardEffect } from '../cardEffects';
import {
  getActivatableSetsunaMagics,
  CHAIN_POINTS,
  KeywordCard,
  getValidAttackTargets as getFlightValidTargets,
  getDirectAttackStatus,
  selectBestTargetForFlight,
} from '../keywordAbilities';
import { updatePlayer, addLog as addLogPure } from './GameState';
import { statusEffectEngine, STATUS_EFFECT_TYPES } from '../statusEffects';
import type { CardWithId, MonsterInstance, HandCard } from '../../types/card';
import type { PlayerNumber } from '../../types/player';
import type { DeepSeaCard, SubmergeContext } from '../deepSeaZone/types';
import {
  canSubmerge,
  getSurfaceableCards as getDeepSeaSurfaceableCards,
  getSurfaceCost,
} from '../deepSeaZone/deepSeaManager';
import { filterEffectiveMagicCards } from '../ai/magicEffectivityChecker';
import { filterEffectiveSkills } from '../ai/skillEffectivityChecker';

// ========================================
// 型定義
// ========================================

/** カード別刹那詠唱統計 */
interface SetsunaCardStat {
  cardId: string;
  name: string;
  attribute: string;
  cost: number;
  p1Opportunities: number;
  p1Activations: number;
  p2Opportunities: number;
  p2Activations: number;
}

/** 刹那詠唱統計 */
interface SetsunaStats {
  p1: { opportunities: number; activations: number };
  p2: { opportunities: number; activations: number };
  cardStats: Record<string, SetsunaCardStat>;
}

/** AI用ゲーム状態スナップショット */
interface AIGameSnapshot {
  myField: (MonsterInstance | null)[];
  opponentField: (MonsterInstance | null)[];
  myHand: CardWithId[];
  opponentHandCount: number;
  myLife: number;
  opponentLife: number;
  myActiveSP: number;
  myGraveyard: CardWithId[];
  turn: number;
  currentPlayer: PlayerNumber;
}

/** チェーンコンテキスト (base.tsのChainContextと互換性を持つ) */
interface ChainContext {
  type?: 'battleStart' | 'attackDeclaration' | 'other';
  attacker?: MonsterInstance;
  target?: MonsterInstance | null;
  [key: string]: unknown;
}

// UsableSkill と ChargeableMonster は '../ai/strategies' からインポート

/** 使用済みアクション追跡 */
interface UsedActionsThisTurn {
  fieldCard: boolean;
  phaseCard: boolean;
  phaseCardCharge: boolean;
  magic: boolean;
}

/** ゲームコンテキスト（AI判断時の状況） */
export interface AIDecisionGameContext {
  myLife?: number;
  opponentLife?: number;
  myFieldCount?: number;
  opponentFieldCount?: number;
  activeSP?: number;
}

/** 代替カード情報 */
export interface AIDecisionAlternative {
  name: string;
  effectType?: string;
  type?: string;  // 'basic' | 'advanced' for skills
  cost?: number;
}

/** AI判断の詳細 */
export interface AIDecisionDetails {
  cardName?: string;
  effectType?: string;
  reason?: string;
  strategyNote?: string;
  gameContext?: AIDecisionGameContext;
  alternatives?: AIDecisionAlternative[];
  [key: string]: unknown;
}

/** AI判断ログエントリ */
export interface AIDecisionEntry {
  turn: number;
  player: PlayerNumber;
  phase: string;
  category: string;
  action: string;
  details: AIDecisionDetails;
  timestamp: number;
}

/** デバッグログインターフェース */
interface DebugLogInterface {
  log: (category: string, action: string, details?: Record<string, unknown>) => void;
  setContext: (turn: number, player: PlayerNumber, phase: string) => void;
  decisions: AIDecisionEntry[];
  getByCategory: (category: string) => AIDecisionEntry[];
  getByTurn: (turn: number) => AIDecisionEntry[];
  getSummary: () => { totalDecisions: number; byCategory: Record<string, number>; byPlayer: Record<number, number> };
  clear: () => void;
  formatForDisplay: () => string;
}

/** AIメインフェイズアクション結果 */
interface AIMainPhaseActionResult {
  action: GameAction;
  actionType?: string;
}

/** AIバトルフェイズアクション結果 */
interface AIBattlePhaseActionResult {
  action: GameAction;
  attackerIndex: number;
}

/** 魔法カード効果分析結果 */
interface MagicCardEffectAnalysis {
  type: string;
  reason: string;
}

/** シミュレーション設定 */
interface SimulationConfig {
  deck1: CardWithId[];
  deck2: CardWithId[];
  firstPlayer?: PlayerNumber;
  maxTurns?: number;
  p1Difficulty?: 'easy' | 'normal' | 'hard' | 'evolved' | 'evolvedSim';
  p2Difficulty?: 'easy' | 'normal' | 'hard' | 'evolved' | 'evolvedSim';
  summoningSickness?: boolean;
  secondPlayerBonusSP?: boolean;
  piercingDamage?: boolean;
  loneWarriorRule?: boolean;
  enableSetsuna?: boolean;
  initialLifePoints?: number;
}

/** シミュレーション結果 */
interface SimulationResult {
  winner: PlayerNumber | 0 | null;
  firstPlayer: PlayerNumber;
  turns: number;
  p1Life: number;
  p2Life: number;
  p1Graveyard: number;
  p2Graveyard: number;
  p1FieldCount: number;
  p2FieldCount: number;
  p1HandCount: number;
  p2HandCount: number;
  p1ActiveSP: number;
  p2ActiveSP: number;
  p1RestedSP: number;
  p2RestedSP: number;
  duration: number;
  logs: GameState['logs'];
  finalState: GameState;
  setsunaStats: SetsunaStats;
}

/** デバッグシミュレーション結果 */
export interface SimulationDebugResult extends Omit<SimulationResult, 'finalState'> {
  aiDecisions: AIDecisionEntry[];
  aiDecisionsSummary: ReturnType<DebugLogInterface['getSummary']>;
  aiDecisionsFormatted: string;
  getMagicDecisions: () => AIDecisionEntry[];
  getSkillDecisions: () => AIDecisionEntry[];
  getSummonDecisions: () => AIDecisionEntry[];
  getAttackDecisions: () => AIDecisionEntry[];
  getSetsunaDecisions: () => AIDecisionEntry[];
}

/** カード統計エントリ */
interface CardStatEntry {
  id: string;
  name: string;
  attribute: string;
  cost: number;
  type: string;
  plays: number;
  wins: number;
  games: number;
}

/** カード統計（勝率・出場率付き） */
interface CardStatWithRates extends CardStatEntry {
  winRate: number;
  playRate: number;
}

/** カード統計サマリー */
interface CardStatsSummary {
  p1: CardStatWithRates[];
  p2: CardStatWithRates[];
}

/** 複数ゲームシミュレーション設定 */
interface MultipleSimulationConfig extends SimulationConfig {
  count?: number;
}

/** 先攻/後攻統計 */
interface FirstPlayerStats {
  p1First: number;
  p2First: number;
  firstWins: number;
  secondWins: number;
  p1WinsWhenFirst: number;
  p1WinsWhenSecond: number;
  p2WinsWhenFirst: number;
  p2WinsWhenSecond: number;
  firstWinRate?: number;
  secondWinRate?: number;
  p1WinRateWhenFirst?: number;
  p1WinRateWhenSecond?: number;
  p2WinRateWhenFirst?: number;
  p2WinRateWhenSecond?: number;
}

/** ライフ統計 */
interface LifeStats {
  winnerLifeTotal: number;
  loserLifeTotal: number;
  winnerLifeList: number[];
  decidedGamesCount: number;
  avgWinnerLife?: number;
  avgLoserLife?: number;
  winnerLifeDistribution?: Record<number, number>;
}

/** SP統計 */
interface SPStats {
  p1TotalActiveSP: number;
  p1TotalRestedSP: number;
  p2TotalActiveSP: number;
  p2TotalRestedSP: number;
  p1TotalCardsUsed: number;
  p2TotalCardsUsed: number;
  avgP1ActiveSP?: number;
  avgP1RestedSP?: number;
  avgP2ActiveSP?: number;
  avgP2RestedSP?: number;
  avgP1CardsUsed?: number;
  avgP2CardsUsed?: number;
  p1Efficiency?: number;
  p2Efficiency?: number;
}

/** 複数ゲームシミュレーション結果 */
export interface MultipleSimulationResult {
  p1Wins: number;
  p2Wins: number;
  draws: number;
  totalTurns: number;
  totalDuration: number;
  games: SimulationResult[];
  firstPlayerStats: FirstPlayerStats;
  turnDistribution: Record<number, number>;
  lifeStats: LifeStats;
  spStats: SPStats;
  setsunaStats: SetsunaStats;
  avgTurns?: number;
  avgDuration?: number;
  p1WinRate?: number;
  p2WinRate?: number;
  cardStats?: CardStatsSummary;
  totalGames?: number;
}

/** オッズ計算結果 */
interface OddsResult {
  p1Odds: number;
  p2Odds: number;
  p1WinRate: number;
  p2WinRate: number;
}


// ========================================
// シミュレーション設定
// ========================================

export const SIMULATION_CONFIG = {
  MAX_TURNS: 100,           // 最大ターン数（無限ループ防止）
  VERBOSE: false,           // ログ出力
  AI_DELAY: 0,              // AIの思考遅延（ヘッドレスでは0）
} as const;

// ========================================
// 刹那詠唱システム
// ========================================

/**
 * 刹那詠唱統計データ構造
 */
export function createSetsunaStats(): SetsunaStats {
  return {
    p1: { opportunities: 0, activations: 0 },
    p2: { opportunities: 0, activations: 0 },
    cardStats: {}, // カードID別統計
  };
}

/**
 * 刹那詠唱機会をカード別に記録
 */
function recordSetsunaOpportunities(setsunaStats: SetsunaStats, availableCards: KeywordCard[], player: PlayerNumber): void {
  for (const card of availableCards) {
    if (!setsunaStats.cardStats[card.id]) {
      setsunaStats.cardStats[card.id] = {
        cardId: card.id,
        name: card.name,
        attribute: card.attribute,
        cost: card.cost,
        p1Opportunities: 0,
        p1Activations: 0,
        p2Opportunities: 0,
        p2Activations: 0,
      };
    }
    if (player === 1) {
      setsunaStats.cardStats[card.id].p1Opportunities++;
    } else {
      setsunaStats.cardStats[card.id].p2Opportunities++;
    }
  }
}

/**
 * 刹那詠唱発動をカード別に記録
 */
function recordSetsunaActivation(setsunaStats: SetsunaStats, card: KeywordCard, player: PlayerNumber): void {
  if (!setsunaStats.cardStats[card.id]) {
    setsunaStats.cardStats[card.id] = {
      cardId: card.id,
      name: card.name,
      attribute: card.attribute,
      cost: card.cost,
      p1Opportunities: 0,
      p1Activations: 0,
      p2Opportunities: 0,
      p2Activations: 0,
    };
  }
  if (player === 1) {
    setsunaStats.cardStats[card.id].p1Activations++;
  } else {
    setsunaStats.cardStats[card.id].p2Activations++;
  }
}

/**
 * AI判断用のゲーム状態スナップショットを作成
 */
function createGameStateSnapshot(state: GameState, player: PlayerNumber): AIGameSnapshot {
  const isP1 = player === 1;
  return {
    myField: isP1 ? state.p1.field : state.p2.field,
    opponentField: isP1 ? state.p2.field : state.p1.field,
    myHand: isP1 ? state.p1.hand : state.p2.hand,
    opponentHandCount: isP1 ? state.p2.hand.length : state.p1.hand.length,
    myLife: isP1 ? state.p1.life : state.p2.life,
    opponentLife: isP1 ? state.p2.life : state.p1.life,
    myActiveSP: isP1 ? state.p1.activeSP : state.p2.activeSP,
    myGraveyard: isP1 ? state.p1.graveyard : state.p2.graveyard,
    turn: state.turn,
    currentPlayer: state.currentPlayer,
  };
}

/**
 * 刹那詠唱カードを発動（相手ターン中）
 */
function activateSetsunaCard(state: GameState, card: CardWithId, activatingPlayer: PlayerNumber): GameState {
  const player = getPlayer(state, activatingPlayer);
  const cardIndex = player.hand.findIndex(c => c.uniqueId === card.uniqueId);

  if (cardIndex === -1) {
    return state;
  }

  // 1. SP消費
  const cost = card.cost || 0;
  const newActiveSP = player.activeSP - cost;
  const newRestedSP = player.restedSP + cost;

  // 2. 手札削除
  const newHand = player.hand.filter((_, i) => i !== cardIndex);

  // 3. 墓地送り
  const newGraveyard = [...player.graveyard, card];

  let newState = updatePlayer(state, activatingPlayer, {
    hand: newHand,
    activeSP: newActiveSP,
    restedSP: newRestedSP,
    graveyard: newGraveyard,
  });

  newState = addLogPure(newState, `【刹那詠唱】P${activatingPlayer}が${card.name}を発動！`, 'info');

  // 4. 効果実行（contextAdapterで currentPlayer を一時的に変更）
  const cardEffect = getCardEffect(card.id);
  if (cardEffect) {
    // 刹那詠唱は発動者視点で効果を実行
    const tempState = { ...newState, currentPlayer: activatingPlayer };
    const context = createPureContext(tempState, { card });
    try {
      // PureContextはCardEffectContextと構造的に互換（setterパターンが異なるが実行時は問題なし）
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      cardEffect(card.effect, context as any);
      newState = context.getState();
      // currentPlayerを元に戻す
      newState = { ...newState, currentPlayer: state.currentPlayer };
    } catch (e) {
      // 効果実行エラーは無視（UIなしでの選択処理など）
      newState = { ...newState, currentPlayer: state.currentPlayer };
    }
  }

  return newState;
}

/**
 * チェーンポイントで刹那詠唱を処理
 */
function processSetsunaChainPoint(
  state: GameState,
  chainPoint: string,
  chainContext: ChainContext,
  opponentStrategy: AIStrategy,
  setsunaStats: SetsunaStats,
  enableSetsuna: boolean,
  debugLog: DebugLogInterface | null
): GameState {
  if (!enableSetsuna) {
    return state;
  }

  // 相手プレイヤー（刹那詠唱を発動する側）
  const opponentPlayer = state.currentPlayer === 1 ? 2 : 1;
  const opponentHand = opponentPlayer === 1 ? state.p1.hand : state.p2.hand;
  const opponentActiveSP = opponentPlayer === 1 ? state.p1.activeSP : state.p2.activeSP;

  // 発動可能な刹那詠唱カードを取得（SPとキーワードのチェック）
  const availableSetsunaRaw = getActivatableSetsunaMagics(opponentHand, opponentActiveSP);

  if (availableSetsunaRaw.length === 0) {
    return state;
  }

  // AI判断用のgameStateを作成（相手プレイヤー視点）
  const tempStateForOpponent: GameState = { ...state, currentPlayer: opponentPlayer };
  const gameStateForOpponent = gameStateToAIGameState(tempStateForOpponent);

  // 効果が有効な刹那詠唱カードのみにフィルタリング（条件を満たさないカードを除外）
  const availableSetsuna = filterEffectiveMagicCards(
    availableSetsunaRaw as CardWithId[],
    gameStateForOpponent
  );

  if (availableSetsuna.length === 0) {
    return state;
  }

  // 発動機会カウント（全体統計）- フィルタ後の有効なカードのみカウント
  const statsKey = opponentPlayer === 1 ? 'p1' : 'p2';
  setsunaStats[statsKey].opportunities++;

  // 発動機会カウント（カード別統計）
  recordSetsunaOpportunities(setsunaStats, availableSetsuna as KeywordCard[], opponentPlayer);

  // AI判断
  const cardToActivate = opponentStrategy.chooseSetsuna(
    availableSetsuna,
    chainContext,
    gameStateForOpponent
  );

  if (cardToActivate) {
    // 発動回数カウント（全体統計）
    setsunaStats[statsKey].activations++;

    // 発動回数カウント（カード別統計）
    recordSetsunaActivation(setsunaStats, cardToActivate, opponentPlayer);

    if (debugLog) {
      debugLog.log('setsuna', 'activated', {
        cardName: cardToActivate.name,
        cardId: cardToActivate.id,
        chainPoint,
        cost: cardToActivate.cost,
      });
    }

    return activateSetsunaCard(state, cardToActivate, opponentPlayer);
  } else {
    if (debugLog) {
      debugLog.log('setsuna', 'skipped', {
        availableCount: availableSetsuna.length,
        chainPoint,
        reason: 'AI chose not to activate',
      });
    }
  }

  return state;
}

// ========================================
// デバッグログシステム
// ========================================

/**
 * AI思考ログを記録するためのクラス
 */
class AIDecisionLog implements DebugLogInterface {
  decisions: AIDecisionEntry[];
  currentTurn: number;
  currentPlayer: PlayerNumber;
  currentPhase: string;

  constructor() {
    this.decisions = [];
    this.currentTurn = 0;
    this.currentPlayer = 1;
    this.currentPhase = '';
  }

  setContext(turn: number, player: PlayerNumber, phase: string): void {
    this.currentTurn = turn;
    this.currentPlayer = player;
    this.currentPhase = phase;
  }

  /**
   * 判断をログに追加
   */
  log(category: string, action: string, details: Record<string, unknown> = {}): void {
    this.decisions.push({
      turn: this.currentTurn,
      player: this.currentPlayer,
      phase: this.currentPhase,
      category,
      action,
      details,
      timestamp: Date.now(),
    });
  }

  /**
   * 特定カテゴリのログを取得
   */
  getByCategory(category: string): AIDecisionEntry[] {
    return this.decisions.filter(d => d.category === category);
  }

  /**
   * 特定ターンのログを取得
   */
  getByTurn(turn: number): AIDecisionEntry[] {
    return this.decisions.filter(d => d.turn === turn);
  }

  /**
   * サマリーを生成
   */
  getSummary(): { totalDecisions: number; byCategory: Record<string, number>; byPlayer: Record<number, number> } {
    const summary: { totalDecisions: number; byCategory: Record<string, number>; byPlayer: Record<number, number> } = {
      totalDecisions: this.decisions.length,
      byCategory: {},
      byPlayer: { 1: 0, 2: 0 },
    };

    for (const d of this.decisions) {
      summary.byCategory[d.category] = (summary.byCategory[d.category] || 0) + 1;
      summary.byPlayer[d.player]++;
    }

    return summary;
  }

  /**
   * ログをクリア
   */
  clear(): void {
    this.decisions = [];
    this.currentTurn = 0;
    this.currentPlayer = 1;
    this.currentPhase = '';
  }

  /**
   * 人間が読みやすい形式でログを出力
   */
  formatForDisplay(): string {
    const lines: string[] = [];
    let lastTurn = -1;
    let lastPlayer = -1;

    for (const d of this.decisions) {
      // ターンヘッダー
      if (d.turn !== lastTurn) {
        lines.push(`\n========== ターン ${d.turn} ==========`);
        lastTurn = d.turn;
        lastPlayer = -1;
      }

      // プレイヤーヘッダー
      if (d.player !== lastPlayer) {
        lines.push(`\n--- P${d.player} のターン ---`);
        lastPlayer = d.player;
      }

      // 判断内容
      const actionSymbol = d.action === 'used' || d.action === 'executed' ? '✓' : '✗';
      const detailStr = this._formatDetails(d.details);
      lines.push(`  [${d.phase}] ${actionSymbol} ${d.category}: ${d.action} ${detailStr}`);
    }

    return lines.join('\n');
  }

  _formatDetails(details: Record<string, unknown>): string {
    if (!details || Object.keys(details).length === 0) return '';

    const parts: string[] = [];
    if (details.cardName) parts.push(`カード: ${details.cardName}`);
    if (details.reason) parts.push(`理由: ${details.reason}`);
    if (details.alternatives) parts.push(`候補: ${(details.alternatives as unknown[]).length}枚`);
    if (details.cost !== undefined) parts.push(`コスト: ${details.cost}`);
    if (details.effect) parts.push(`効果: ${(details.effect as string).substring(0, 30)}...`);
    if (details.score !== undefined) parts.push(`スコア: ${details.score}`);

    return parts.length > 0 ? `(${parts.join(', ')})` : '';
  }
}

// ========================================
// GameState → AIGameState アダプター
// ========================================

/**
 * GameEngine の state を対戦AI用の AIGameState 形式に変換
 */
function gameStateToAIGameState(state: GameState): AIGameState {
  const isP1 = state.currentPlayer === 1;
  const my = isP1 ? state.p1 : state.p2;
  const opp = isP1 ? state.p2 : state.p1;

  return {
    currentPlayer: state.currentPlayer,
    phase: state.phase,
    turn: state.turn,
    isFirstTurn: state.isFirstTurn,
    firstPlayer: state.firstPlayer,

    myLife: my.life,
    opponentLife: opp.life,
    myHand: my.hand,
    opponentHandCount: opp.hand.length,
    myField: my.field,
    opponentField: opp.field,
    myGraveyard: my.graveyard,
    opponentGraveyard: opp.graveyard,
    myDeckCount: my.deck.length,
    opponentDeckCount: opp.deck.length,
    myDeck: my.deck,
    opponentDeck: opp.deck,

    myActiveSP: my.activeSP,
    myRestedSP: my.restedSP,
    myBonusSP: my.bonusSP || 0,
    myFieldCard: my.fieldCard,
    myPhaseCard: my.phaseCard,
    opponentFieldCard: opp.fieldCard,
    opponentPhaseCard: opp.phaseCard,
    myStatusEffects: my.statusEffects,
    // 深海領域
    myDeepSeaZone: my.deepSeaZone,
    opponentDeepSeaZone: opp.deepSeaZone,
  };
}

// ========================================
// ストラテジー対応AI関数
// ========================================

/**
 * 召喚可能なカードを取得
 */
function getSummonableCards(state: GameState): CardWithId[] {
  const player = getCurrentPlayer(state);
  const slotIndex = player.field.findIndex(monster => monster === null);
  if (slotIndex < 0) return [];
  return player.hand.filter(card => canSummonCard(state, card, slotIndex));
}

/**
 * 空きスロットを取得
 */
function getEmptySlots(aiGameState: AIGameState): number[] {
  return aiGameState.myField
    .map((m, i) => m === null ? i : -1)
    .filter(i => i >= 0);
}

/**
 * 使用可能なスキルを取得
 */
function getUsableSkills(aiGameState: AIGameState): UsableSkill[] {
  const usableSkills: UsableSkill[] = [];

  aiGameState.myField.forEach((monster, index) => {
    if (!monster) return;
    if (monster.usedSkillThisTurn) return;

    const chargesCount = monster.charges?.length || 0;

    // 上級技（チャージ2個以上）
    if (chargesCount >= 2 && monster.advancedSkill) {
      usableSkills.push({
        monsterIndex: index,
        skillType: 'advanced',
        monster,
      });
    }

    // 基本技（チャージ1個以上）
    if (chargesCount >= 1 && monster.basicSkill) {
      usableSkills.push({
        monsterIndex: index,
        skillType: 'basic',
        monster,
      });
    }
  });

  // 効果が有効なスキルのみを返す（条件を満たさないスキルを除外）
  return filterEffectiveSkills(usableSkills, aiGameState);
}

/**
 * チャージ可能なモンスターを取得
 */
function getChargeableMonsters(aiGameState: AIGameState): ChargeableMonster[] {
  const chargeableMonsters: ChargeableMonster[] = [];

  aiGameState.myField.forEach((monster, index) => {
    if (!monster) return;
    const charges = monster.charges?.length || 0;
    const hasSkill = monster.basicSkill || monster.advancedSkill;
    if (charges < 2 && hasSkill) {
      chargeableMonsters.push({
        monsterIndex: index,
        monster,
        currentCharges: charges,
      });
    }
  });

  return chargeableMonsters;
}

/**
 * チャージに使用可能なカードを取得
 */
function getChargeableCards(aiGameState: AIGameState): CardWithId[] {
  return aiGameState.myHand.filter(card => {
    return card.type === 'monster' || card.type === 'magic' || card.type === 'field' || card.type === 'phasecard';
  });
}

/**
 * 使用可能な魔法カードを取得
 */
function getUsableMagicCards(aiGameState: AIGameState): CardWithId[] {
  const { myHand, myActiveSP, myBonusSP } = aiGameState;
  const totalAvailableSP = myActiveSP + (myBonusSP || 0);

  // SPで使用可能な魔法カードをフィルタリング
  const spUsable = myHand.filter(card => {
    if (card.type !== 'magic') return false;
    const cost = card.cost + (card.tempCostModifier || 0);
    return cost <= totalAvailableSP;
  });

  // 効果が有効な魔法カードのみを返す（条件を満たさないカードを除外）
  return filterEffectiveMagicCards(spUsable, aiGameState);
}

/**
 * 配置可能なフィールドカードを取得
 */
function getPlaceableFieldCards(aiGameState: AIGameState): CardWithId[] {
  const { myHand, myActiveSP, myBonusSP, myFieldCard } = aiGameState;
  const totalAvailableSP = myActiveSP + (myBonusSP || 0);

  // 既にフィールドカードがある場合は配置しない（上書きは避ける）
  if (myFieldCard) return [];

  return myHand.filter(card => {
    if (card.type !== 'field') return false;
    const cost = card.cost + (card.tempCostModifier || 0);
    return cost <= totalAvailableSP;
  });
}

/**
 * 配置可能なフェイズカードを取得
 */
function getPlaceablePhaseCards(aiGameState: AIGameState): CardWithId[] {
  const { myHand, myActiveSP, myBonusSP, myPhaseCard } = aiGameState;
  const totalAvailableSP = myActiveSP + (myBonusSP || 0);

  // 既にフェイズカードがある場合は配置しない
  if (myPhaseCard) return [];

  return myHand.filter(card => {
    if (card.type !== 'phasecard') return false;
    const cost = card.cost + (card.tempCostModifier || 0);
    return cost <= totalAvailableSP;
  });
}

/**
 * フェイズカードにチャージ可能なカードを取得
 * 同属性 or 「なし」属性 or フェイズカードが「なし」属性なら全カード
 */
function getPhaseCardChargeableCards(aiGameState: AIGameState): CardWithId[] {
  const { myHand, myPhaseCard } = aiGameState;

  // フェイズカードが設置されていない、または最終段階の場合は空配列
  if (!myPhaseCard) return [];
  const currentStage = myPhaseCard.stage !== undefined ? myPhaseCard.stage : (myPhaseCard.charges?.length || 0);
  if (currentStage >= 3) return [];

  const isNoneAttribute = myPhaseCard.attribute === 'なし';

  return myHand.filter(card => {
    // チャージ可能なカード種類
    if (!['monster', 'magic', 'field', 'phase'].includes(card.type)) return false;
    // フェイズカードが「なし」属性なら全カードチャージ可能
    if (isNoneAttribute) return true;
    // 同属性 or 「なし」属性カード
    return card.attribute === myPhaseCard.attribute || card.attribute === 'なし';
  });
}

/**
 * 潜航可能な手札カードを取得
 */
function getSubmergibleCards(aiGameState: AIGameState, turn: number): HandCard[] {
  const { myHand, myDeepSeaZone } = aiGameState;

  // 深海領域がない or 開放されていない場合は空配列
  if (!myDeepSeaZone || !myDeepSeaZone.isUnlocked) return [];

  const context: SubmergeContext = {
    deepSeaZone: myDeepSeaZone,
    allowWaterAttribute: false, // フェイズカード効果は別途判定（AI用は簡易版）
    currentTurn: turn,
  };

  return myHand.filter(card => canSubmerge(card, context)) as HandCard[];
}

/**
 * 浮上可能な深海カードを取得
 */
function getSurfaceableCards(aiGameState: AIGameState): DeepSeaCard[] {
  const { myDeepSeaZone, myField, myActiveSP, myBonusSP } = aiGameState;

  // 深海領域がない場合は空配列
  if (!myDeepSeaZone || !myDeepSeaZone.isUnlocked) return [];

  const emptySlots = myField.filter(m => m === null).length;
  if (emptySlots === 0) return [];

  const totalAvailableSP = myActiveSP + (myBonusSP || 0);
  // costReduction は 0 とする（ヴェルゼファール等の効果は別途処理が必要）
  return getDeepSeaSurfaceableCards(myDeepSeaZone, totalAvailableSP, emptySlots, 0);
}

/**
 * AI用：CAGED状態のモンスターをSP1で解除
 * SPがあれば全てのCAGEDモンスターを解除する（AIは基本的に解除を選ぶ）
 */
function processCagedRelease(state: GameState): GameState {
  let newState = state;
  const playerKey = state.currentPlayer === 1 ? 'p1' : 'p2';
  const player = newState[playerKey];

  // CAGED状態のモンスターを探す
  for (let i = 0; i < player.field.length; i++) {
    const monster = player.field[i];
    if (monster && statusEffectEngine.hasStatus(monster, STATUS_EFFECT_TYPES.CAGED)) {
      // SPが足りなければ終了
      if (player.activeSP < 1) break;

      // SP消費
      const newPlayer = {
        ...player,
        activeSP: player.activeSP - 1,
        restedSP: player.restedSP + 1,
        field: player.field.map((m, idx) => {
          if (idx === i && m) {
            const newStatusEffects = (m.statusEffects || []).filter(
              s => s.type !== STATUS_EFFECT_TYPES.CAGED
            );
            return { ...m, statusEffects: newStatusEffects };
          }
          return m;
        }),
      };

      newState = {
        ...newState,
        [playerKey]: newPlayer,
        logs: [...newState.logs, { message: `SP1を払って${monster.name}を檻から解放した！`, type: 'heal' }],
      };

      // playerを更新（次のループ用）
      player.activeSP = newPlayer.activeSP;
      player.restedSP = newPlayer.restedSP;
      player.field = newPlayer.field;
    }
  }

  return newState;
}

/**
 * 攻撃可能なモンスターのインデックスを取得
 */
function getAttackableMonsters(aiGameState: AIGameState): number[] {
  const attackable: number[] = [];

  aiGameState.myField.forEach((monster, index) => {
    // canMonsterAttackで状態異常（CAGED等）もチェック
    if (canMonsterAttack(monster)) {
      attackable.push(index);
    }
  });

  return attackable;
}

/**
 * 有効な攻撃対象を取得
 * 飛行/対空システムによる攻撃対象制限を考慮
 */
function getValidAttackTargets(aiGameState: AIGameState, state: GameState, attacker?: MonsterInstance): (number | 'direct')[] {
  const targets: (number | 'direct')[] = [];

  if (attacker) {
    // 飛行/対空システムによる攻撃対象制限を適用
    const validMonsterTargets = getFlightValidTargets(attacker, aiGameState.opponentField);
    targets.push(...validMonsterTargets);

    // ダイレクトアタック可否を判定
    const directStatus = getDirectAttackStatus(attacker, aiGameState.opponentField);
    const player = getCurrentPlayer(state);

    if (directStatus.canDirect && statusEffectEngine.canDirectAttack(player.statusEffects)) {
      targets.push('direct');
    }
  } else {
    // 攻撃者未指定の場合は全モンスターを対象に（後方互換性）
    aiGameState.opponentField.forEach((monster, index) => {
      if (monster) {
        targets.push(index);
      }
    });

    // ダイレクトアタック禁止チェック
    const player = getCurrentPlayer(state);
    if (statusEffectEngine.canDirectAttack(player.statusEffects)) {
      targets.push('direct');
    }
  }

  return targets;
}

/**
 * ストラテジーを使ったAI判断（メインフェイズ）
 */
function getAIMainPhaseAction(
  state: GameState,
  strategy: AIStrategy,
  chargeUsedThisTurn: boolean = false,
  usedActionsThisTurn: Partial<UsedActionsThisTurn> = {},
  debugLog: DebugLogInterface | null = null
): AIMainPhaseActionResult | GameAction | null {
  const aiGameState = gameStateToAIGameState(state);
  const player = getCurrentPlayer(state);

  // 1. 使用可能なスキルを判断
  const usableSkills = getUsableSkills(aiGameState);
  const skillChoice = strategy.chooseSkill(usableSkills, aiGameState);
  if (skillChoice) {
    if (debugLog) {
      debugLog.log('skill', 'executed', {
        cardName: skillChoice.monster?.name,
        skillType: skillChoice.skillType,
        alternatives: usableSkills,
        reason: skillChoice.skillType === 'advanced' ? '上級技が使用可能' : '基本技を使用',
      });
    }
    return actions.executeSkill(skillChoice.monsterIndex, skillChoice.skillType);
  } else if (debugLog && usableSkills.length > 0) {
    debugLog.log('skill', 'skipped', {
      reason: 'スキル使用を見送り',
      alternatives: usableSkills.map(s => ({ name: s.monster?.name, type: s.skillType })),
    });
  }

  // 2. 召喚/浮上判断（効率比較して最適な方を選択）
  // 深度3以上の浮上はコスト軽減+ATKボーナスで通常召喚より効率的なことが多い
  const summonableCards = getSummonableCards(state);
  const surfaceableCards = getSurfaceableCards(aiGameState);
  const emptySlots = getEmptySlots(aiGameState);

  // 効率計算ヘルパー: (ATK + ボーナス) / コスト
  const DEPTH_ATK_BONUS = 200; // 深度1あたりのATKボーナス
  const calcSummonEfficiency = (card: CardWithId): number => {
    const cost = card.cost || 1;
    const atk = card.attack || 0;
    return atk / cost;
  };
  const calcSurfaceEfficiency = (deepSeaCard: DeepSeaCard): number => {
    const surfaceCost = getSurfaceCost(deepSeaCard, 0);
    const atk = (deepSeaCard.card.attack || 0) + (deepSeaCard.depth * DEPTH_ATK_BONUS);
    return atk / surfaceCost;
  };

  // 最適な召喚カードを選択
  let bestSummonCard: CardWithId | null = null;
  let bestSummonEfficiency = 0;
  const cardToSummon = strategy.chooseSummon(summonableCards, aiGameState);
  if (cardToSummon) {
    bestSummonCard = cardToSummon;
    bestSummonEfficiency = calcSummonEfficiency(cardToSummon);
  }

  // 最適な浮上カードを選択（深度で判定するchooseSurfaceを使用）
  let bestSurfaceChoice: { deepSeaCard: DeepSeaCard; fieldSlot: number } | null = null;
  let bestSurfaceEfficiency = 0;
  if (surfaceableCards.length > 0 && strategy.chooseSurface && emptySlots.length > 0) {
    const surfaceChoice = strategy.chooseSurface(surfaceableCards, aiGameState);
    if (surfaceChoice) {
      bestSurfaceChoice = surfaceChoice;
      bestSurfaceEfficiency = calcSurfaceEfficiency(surfaceChoice.deepSeaCard);
    }
  }

  // 効率を比較して、より効率的な方を実行
  // 浮上が選択されていて、召喚より効率が良い場合は浮上優先
  if (bestSurfaceChoice && bestSurfaceEfficiency >= bestSummonEfficiency) {
    const { deepSeaCard, fieldSlot } = bestSurfaceChoice;
    const deepSeaIndex = aiGameState.myDeepSeaZone?.cards.findIndex(
      c => c.uniqueId === deepSeaCard.uniqueId
    ) ?? -1;
    if (deepSeaIndex !== -1) {
      if (debugLog) {
        debugLog.log('surface', 'executed', {
          cardName: deepSeaCard.card.name,
          depth: deepSeaCard.depth,
          surfaceCost: getSurfaceCost(deepSeaCard, 0),
          atkWithBonus: (deepSeaCard.card.attack || 0) + (deepSeaCard.depth * DEPTH_ATK_BONUS),
          efficiency: bestSurfaceEfficiency.toFixed(1),
          fieldSlot,
          comparedToSummon: bestSummonCard ? {
            name: bestSummonCard.name,
            efficiency: bestSummonEfficiency.toFixed(1),
          } : null,
          alternatives: surfaceableCards.map(c => ({
            name: c.card.name,
            depth: c.depth,
            efficiency: calcSurfaceEfficiency(c).toFixed(1),
          })),
          reason: `深度${deepSeaCard.depth}で浮上（効率${bestSurfaceEfficiency.toFixed(1)} >= 召喚${bestSummonEfficiency.toFixed(1)}）`,
        });
      }
      return actions.surfaceCard(state.currentPlayer, deepSeaIndex, fieldSlot, 0);
    }
  }

  // 召喚が選択されている場合
  if (bestSummonCard && emptySlots.length > 0) {
    const slot = strategy.chooseSlot(emptySlots, bestSummonCard, aiGameState);
    if (slot !== null) {
      const handIndex = player.hand.findIndex(c => c.uniqueId === bestSummonCard!.uniqueId);
      if (handIndex !== -1) {
        if (debugLog) {
          debugLog.log('summon', 'executed', {
            cardName: bestSummonCard.name,
            cost: bestSummonCard.cost,
            attack: bestSummonCard.attack,
            hp: bestSummonCard.hp,
            efficiency: bestSummonEfficiency.toFixed(1),
            slot: slot,
            comparedToSurface: bestSurfaceChoice ? {
              name: bestSurfaceChoice.deepSeaCard.card.name,
              depth: bestSurfaceChoice.deepSeaCard.depth,
              efficiency: bestSurfaceEfficiency.toFixed(1),
            } : null,
            alternatives: summonableCards.map(c => c.name),
            reason: `攻撃力/コスト比で選択（効率${bestSummonEfficiency.toFixed(1)}）`,
          });
        }
        return actions.summonCard(handIndex, slot);
      }
    }
  } else if (debugLog && (summonableCards.length > 0 || surfaceableCards.length > 0)) {
    debugLog.log('summon', 'skipped', {
      reason: '召喚/浮上を見送り',
      summonAlternatives: summonableCards.map(c => ({ name: c.name, cost: c.cost })),
      surfaceAlternatives: surfaceableCards.map(c => ({ name: c.card.name, depth: c.depth })),
      activeSP: aiGameState.myActiveSP,
    });
  }

  // 3. フィールドカード配置判断（1ターン1回）
  if (!usedActionsThisTurn.fieldCard) {
    const placeableFieldCards = getPlaceableFieldCards(aiGameState);
    const fieldCardToPlace = strategy.chooseFieldCard(placeableFieldCards, aiGameState);
    if (fieldCardToPlace) {
      const cardIndex = player.hand.findIndex(c => c.uniqueId === fieldCardToPlace.uniqueId);
      if (cardIndex !== -1) {
        if (debugLog) {
          debugLog.log('fieldCard', 'placed', {
            cardName: fieldCardToPlace.name,
            cost: fieldCardToPlace.cost,
            effect: fieldCardToPlace.effect,
            alternatives: placeableFieldCards.map(c => c.name),
          });
        }
        return { action: actions.placeFieldCard(cardIndex), actionType: 'fieldCard' };
      }
    } else if (debugLog && placeableFieldCards.length > 0) {
      debugLog.log('fieldCard', 'skipped', {
        reason: 'フィールドカード配置を見送り',
        alternatives: placeableFieldCards.map(c => ({ name: c.name, attribute: c.attribute })),
      });
    }
  }

  // 4. フェイズカード配置判断（1ターン1回）
  if (!usedActionsThisTurn.phaseCard) {
    const placeablePhaseCards = getPlaceablePhaseCards(aiGameState);
    const phaseCardToPlace = strategy.choosePhaseCard(placeablePhaseCards, aiGameState);
    if (phaseCardToPlace) {
      const cardIndex = player.hand.findIndex(c => c.uniqueId === phaseCardToPlace.uniqueId);
      if (cardIndex !== -1) {
        if (debugLog) {
          debugLog.log('phaseCard', 'placed', {
            cardName: phaseCardToPlace.name,
            cost: phaseCardToPlace.cost,
            effect: phaseCardToPlace.effect,
          });
        }
        return { action: actions.placePhaseCard(cardIndex), actionType: 'phaseCard' };
      }
    } else if (debugLog && placeablePhaseCards.length > 0) {
      debugLog.log('phaseCard', 'skipped', {
        reason: 'フェイズカード配置を見送り',
        alternatives: placeablePhaseCards.map(c => ({ name: c.name, attribute: c.attribute })),
      });
    }
  }

  // 5. 魔法カード使用判断（複数回使用可能）
  if (!usedActionsThisTurn.magic) {
    const usableMagicCards = getUsableMagicCards(aiGameState);
    const magicCardToUse = strategy.chooseMagicCard(usableMagicCards, aiGameState);
    if (magicCardToUse) {
      const cardIndex = player.hand.findIndex(c => c.uniqueId === magicCardToUse.uniqueId);
      if (cardIndex !== -1) {
        if (debugLog) {
          // 魔法カードの詳細な判断理由を記録
          const effectAnalysis = analyzeMagicCardEffect(magicCardToUse);
          debugLog.log('magic', 'used', {
            cardName: magicCardToUse.name,
            cost: magicCardToUse.cost,
            effect: magicCardToUse.effect,
            effectType: effectAnalysis.type,
            reason: effectAnalysis.reason,
            alternatives: usableMagicCards.map(c => ({
              name: c.name,
              cost: c.cost,
              effectType: analyzeMagicCardEffect(c).type,
            })),
            gameContext: {
              myLife: aiGameState.myLife,
              opponentLife: aiGameState.opponentLife,
              myFieldCount: aiGameState.myField.filter(m => m).length,
              opponentFieldCount: aiGameState.opponentField.filter(m => m).length,
            },
          });
        }
        return { action: actions.useMagic(cardIndex), actionType: 'magic' };
      }
    } else if (debugLog && usableMagicCards.length > 0) {
      // 魔法カードを使わなかった理由を詳細に記録
      debugLog.log('magic', 'skipped', {
        reason: '魔法カード使用を見送り',
        alternatives: usableMagicCards.map(c => ({
          name: c.name,
          cost: c.cost,
          effect: c.effect?.substring(0, 50),
          effectType: analyzeMagicCardEffect(c).type,
        })),
        gameContext: {
          myLife: aiGameState.myLife,
          opponentLife: aiGameState.opponentLife,
          myFieldCount: aiGameState.myField.filter(m => m).length,
          opponentFieldCount: aiGameState.opponentField.filter(m => m).length,
          activeSP: aiGameState.myActiveSP,
        },
        strategyNote: 'AIの判断基準により見送り（確率判定またはスコア不足）',
      });
    }
  }

  // 6. モンスターチャージ判断
  if (!chargeUsedThisTurn && !state.turnFlags?.chargeUsedThisTurn) {
    const chargeableCards = getChargeableCards(aiGameState);
    const chargeableMonsters = getChargeableMonsters(aiGameState);
    const chargeChoice = strategy.chooseCharge(chargeableCards, chargeableMonsters, aiGameState);
    if (chargeChoice) {
      const cardIndex = player.hand.findIndex(c => c.uniqueId === chargeChoice.card.uniqueId);
      if (cardIndex !== -1) {
        if (debugLog) {
          const targetMonster = aiGameState.myField[chargeChoice.monsterIndex];
          debugLog.log('charge', 'executed', {
            cardName: chargeChoice.card.name,
            targetMonsterName: targetMonster?.name,
            currentCharges: targetMonster?.charges?.length || 0,
            reason: '技使用のためチャージ',
          });
        }
        return actions.chargeCard(cardIndex, chargeChoice.monsterIndex);
      }
    } else if (debugLog && chargeableCards.length > 0 && chargeableMonsters.length > 0) {
      debugLog.log('charge', 'skipped', {
        reason: 'チャージを見送り',
        chargeableCards: chargeableCards.map(c => c.name),
        chargeableMonsters: chargeableMonsters.map(m => ({
          name: m.monster?.name,
          charges: m.currentCharges,
        })),
      });
    }
  }

  // 7. フェイズカードチャージ判断（モンスターチャージの後）
  if (!chargeUsedThisTurn && !state.turnFlags?.chargeUsedThisTurn && !usedActionsThisTurn.phaseCardCharge) {
    const phaseCardChargeableCards = getPhaseCardChargeableCards(aiGameState);
    if (phaseCardChargeableCards.length > 0 && strategy.choosePhaseCardCharge) {
      const phaseChargeChoice = strategy.choosePhaseCardCharge(phaseCardChargeableCards, aiGameState);
      if (phaseChargeChoice) {
        const cardIndex = player.hand.findIndex(c => c.uniqueId === phaseChargeChoice.uniqueId);
        if (cardIndex !== -1) {
          if (debugLog) {
            debugLog.log('phaseCardCharge', 'executed', {
              cardName: phaseChargeChoice.name,
              phaseCardName: aiGameState.myPhaseCard?.name,
              currentStage: aiGameState.myPhaseCard?.stage || aiGameState.myPhaseCard?.charges?.length || 0,
              reason: 'フェイズカード段階強化',
            });
          }
          return { action: actions.chargePhaseCard(cardIndex), actionType: 'phaseCardCharge' };
        }
      } else if (debugLog && phaseCardChargeableCards.length > 0) {
        debugLog.log('phaseCardCharge', 'skipped', {
          reason: 'フェイズカードチャージを見送り',
          chargeableCards: phaseCardChargeableCards.map(c => c.name),
          phaseCardName: aiGameState.myPhaseCard?.name,
        });
      }
    }
  }

  // 8. 潜航判断（手札から深海領域へ）
  const submergibleCards = getSubmergibleCards(aiGameState, state.turn);
  if (submergibleCards.length > 0 && strategy.chooseSubmerge) {
    const submergeChoice = strategy.chooseSubmerge(submergibleCards, aiGameState);
    if (submergeChoice) {
      // 手札内のインデックスを取得
      const cardIndex = player.hand.findIndex(c => c.uniqueId === submergeChoice.uniqueId);
      if (cardIndex !== -1) {
        if (debugLog) {
          debugLog.log('submerge', 'executed', {
            cardName: submergeChoice.name,
            alternatives: submergibleCards.map(c => c.name),
            reason: '潜航キーワードで深海領域へ',
          });
        }
        return actions.submergeCard(state.currentPlayer, cardIndex);
      }
    }
  }

  // 9. アクションなし → 次のフェイズへ
  if (debugLog) {
    debugLog.log('phase', 'end_main', {
      reason: '実行可能なアクションなし',
    });
  }
  return null;
}

/**
 * 魔法カードの効果を分析
 */
function analyzeMagicCardEffect(card: CardWithId): MagicCardEffectAnalysis {
  const effect = card.effect || '';

  // ダメージ系
  if (effect.includes('ダメージ')) {
    const match = effect.match(/(\d+)ダメージ/);
    const damage = match ? match[1] : '?';
    return { type: 'damage', reason: `${damage}ダメージを与える` };
  }

  // 回復系
  if (effect.includes('回復')) {
    const match = effect.match(/(\d+)回復/);
    const heal = match ? match[1] : '?';
    return { type: 'heal', reason: `${heal}ライフを回復` };
  }

  // ドロー系
  if (effect.includes('ドロー') || effect.includes('引く')) {
    const match = effect.match(/(\d+)枚/);
    const count = match ? match[1] : '?';
    return { type: 'draw', reason: `${count}枚ドロー` };
  }

  // 破壊系
  if (effect.includes('破壊')) {
    return { type: 'destroy', reason: 'モンスター破壊効果' };
  }

  // バフ/デバフ系
  if (effect.includes('攻撃力') || effect.includes('HP')) {
    if (effect.includes('アップ') || effect.includes('+') || effect.includes('上昇')) {
      return { type: 'buff', reason: 'ステータス強化' };
    }
    if (effect.includes('ダウン') || effect.includes('-') || effect.includes('減少')) {
      return { type: 'debuff', reason: 'ステータス弱体化' };
    }
  }

  // 特殊召喚系
  if (effect.includes('蘇生') || effect.includes('墓地から')) {
    return { type: 'revive', reason: 'モンスター蘇生' };
  }

  // サーチ系
  if (effect.includes('サーチ') || effect.includes('手札に加える')) {
    return { type: 'search', reason: 'カードサーチ' };
  }

  // その他
  return { type: 'other', reason: '特殊効果' };
}

/**
 * ストラテジーを使ったAI判断（バトルフェイズ）
 */
function getAIBattlePhaseAction(
  state: GameState,
  strategy: AIStrategy,
  attackedMonsters: Set<number> = new Set(),
  debugLog: DebugLogInterface | null = null
): AIBattlePhaseActionResult | null {
  const aiGameState = gameStateToAIGameState(state);

  // 先攻1ターン目は攻撃不可
  if (state.isFirstTurn && state.currentPlayer === state.firstPlayer) {
    if (debugLog) {
      debugLog.log('attack', 'skipped', { reason: '先攻1ターン目は攻撃不可' });
    }
    return null;
  }

  // 攻撃可能なモンスターを取得（攻撃済みを除く）
  const attackableIndices = getAttackableMonsters(aiGameState)
    .filter(idx => !attackedMonsters.has(idx));

  if (attackableIndices.length === 0) {
    if (debugLog) {
      debugLog.log('attack', 'none', { reason: '攻撃可能なモンスターなし' });
    }
    return null;
  }

  // 攻撃順序を決定
  const attackOrder = strategy.chooseAttackOrder(attackableIndices, aiGameState);

  if (attackOrder.length === 0) {
    return null;
  }

  const attackerIndex = attackOrder[0];
  const attacker = aiGameState.myField[attackerIndex];

  // attackerが存在しない場合は攻撃をスキップ
  if (!attacker) return null;

  // 攻撃対象を選択（飛行/対空システムを考慮）
  const validTargets = getValidAttackTargets(aiGameState, state, attacker);

  // 有効な攻撃対象がない場合はスキップ
  if (validTargets.length === 0) {
    if (debugLog) {
      debugLog.log('attack', 'skipped', {
        attackerName: attacker.name,
        reason: '攻撃可能な対象なし（飛行/対空制限）',
      });
    }
    return null;
  }

  const target = strategy.chooseAttackTarget(validTargets, attacker, aiGameState);

  // targetが 'direct' の場合は -1 に変換
  const targetIndex = target === 'direct' ? -1 : target;

  if (debugLog) {
    const targetName = targetIndex === -1
      ? 'ダイレクトアタック'
      : aiGameState.opponentField[targetIndex]?.name || 'モンスター';
    debugLog.log('attack', 'executed', {
      attackerName: attacker?.name,
      attackerAttack: attacker?.currentAttack || attacker?.attack,
      targetName,
      targetIndex,
      reason: targetIndex === -1 ? '直接攻撃を選択' : 'モンスターを攻撃',
      validTargets: validTargets.map(t => t === 'direct' ? 'direct' : aiGameState.opponentField[t]?.name),
    });
  }

  return {
    action: actions.attack(attackerIndex, targetIndex),
    attackerIndex,
  };
}

// ========================================
// シミュレーション実行
// ========================================

/**
 * 1ターンを実行
 */
function executeTurn(
  state: GameState,
  p1Strategy: AIStrategy,
  p2Strategy: AIStrategy,
  debugLog: DebugLogInterface | null = null,
  setsunaStats: SetsunaStats | null = null,
  enableSetsuna: boolean = true
): GameState {
  let newState = state;
  const strategy = state.currentPlayer === 1 ? p1Strategy : p2Strategy;
  const opponentStrategy = state.currentPlayer === 1 ? p2Strategy : p1Strategy;

  // デバッグログにコンテキストを設定
  if (debugLog) {
    debugLog.setContext(state.turn, state.currentPlayer, 'turn_start');
  }

  // ターン開始
  newState = applyAction(newState, actions.processPhase(PHASES.TURN_START));

  // 後攻ボーナスSP付与（後攻プレイヤーの初回ターンのみ）
  if (newState.config?.secondPlayerBonusSP && newState.isFirstTurn && newState.currentPlayer !== newState.firstPlayer) {
    const playerKey = newState.currentPlayer === 1 ? 'p1' : 'p2';
    newState = {
      ...newState,
      [playerKey]: {
        ...newState[playerKey],
        bonusSP: 1,
      },
      logs: [...newState.logs, { message: `☆後攻ボーナス: P${newState.currentPlayer}にボーナスSP+1！`, type: 'heal' }],
    };
  }

  // 後攻ボーナス終了：後攻の3ターン目開始時にSPを1減らす（以降は先攻と同じペースに）
  if (newState.turn === 3 && newState.currentPlayer !== newState.firstPlayer) {
    const playerKey = newState.currentPlayer === 1 ? 'p1' : 'p2';
    newState = {
      ...newState,
      [playerKey]: {
        ...newState[playerKey],
        activeSP: Math.max(0, newState[playerKey].activeSP - 1),
      },
      logs: [...newState.logs, { message: `後攻ボーナス終了: P${newState.currentPlayer}のSPが1減少`, type: 'info' }],
    };
  }

  // ターン開始トリガー発火
  newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_TURN_START_SELF, {});

  // AI用：CAGED状態のモンスターをSP1で解除
  newState = processCagedRelease(newState);

  newState = applyAction(newState, actions.nextPhase());

  // ドロー
  if (debugLog) {
    debugLog.setContext(state.turn, state.currentPlayer, 'draw');
  }
  newState = applyAction(newState, actions.processPhase(PHASES.DRAW));
  // ドローフェイズトリガー発火
  newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_DRAW_PHASE_SELF, {});
  // 相手のON_OPPONENT_DRAWトリガーを発火（虚蝕の呪詛等のフェイズカード効果）
  // currentPlayerがドローした側なので、opponent視点のトリガーが発火する
  newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_OPPONENT_DRAW, {});
  newState = applyAction(newState, actions.nextPhase());

  // メインフェイズ
  if (debugLog) {
    debugLog.setContext(newState.turn, newState.currentPlayer, 'main');
    // ターン開始時の手札・フィールド状況をログ
    const currentPlayer = getCurrentPlayer(newState);
    const opponent = newState.currentPlayer === 1 ? newState.p2 : newState.p1;
    debugLog.log('status', 'turn_info', {
      hand: currentPlayer.hand.map(c => ({
        name: c.name,
        type: c.type,
        cost: c.cost,
        attribute: c.attribute,
        attack: c.attack,
        hp: c.hp,
      })),
      handCount: currentPlayer.hand.length,
      activeSP: currentPlayer.activeSP,
      restedSP: currentPlayer.restedSP,
      bonusSP: currentPlayer.bonusSP || 0,
      life: currentPlayer.life,
      opponentLife: opponent.life,
      myField: currentPlayer.field.map((m, i) => m ? {
        slot: i,
        name: m.name,
        currentAttack: m.currentAttack || m.attack,
        currentHp: m.currentHp || m.hp,
        charges: m.charges?.length || 0,
      } : null).filter(Boolean),
      opponentField: opponent.field.map((m, i) => m ? {
        slot: i,
        name: m.name,
        currentAttack: m.currentAttack || m.attack,
        currentHp: m.currentHp || m.hp,
      } : null).filter(Boolean),
      deckCount: currentPlayer.deck.length,
      graveyardCount: currentPlayer.graveyard.length,
    });
  }
  // ON_MAIN_PHASE_SELFは任意発動（OPTIONAL）なので自動発火しない
  // TODO: AIが任意発動トリガーを使用するか判断する処理を追加

  let mainPhaseActions = 0;
  const maxMainActions = 30; // 無限ループ防止（魔法/フィールド/フェイズ追加のため増加）
  let chargeUsedThisTurn = false;
  const usedActionsThisTurn = {
    fieldCard: false,      // フィールドカードは1ターン1回
    phaseCard: false,      // フェイズカードは1ターン1回
    phaseCardCharge: false, // フェイズカードチャージは1ターン1回
    magic: false,          // 魔法カードは戦略次第で1回（無限ループ防止）
  };

  while (mainPhaseActions < maxMainActions && newState.gameStatus !== GAME_STATUS.GAME_OVER) {
    const result = getAIMainPhaseAction(newState, strategy, chargeUsedThisTurn, usedActionsThisTurn, debugLog);
    if (!result) break;

    // 戻り値が { action, actionType } 形式か直接アクションか判定
    const isWrapped = 'action' in result && result.action !== undefined;
    const actualAction = isWrapped ? (result as AIMainPhaseActionResult).action : result as GameAction;
    const actionType = isWrapped ? (result as AIMainPhaseActionResult).actionType : undefined;

    // アクションタイプに応じてフラグを立てる
    if (actionType === 'fieldCard') {
      usedActionsThisTurn.fieldCard = true;
    } else if (actionType === 'phaseCard') {
      usedActionsThisTurn.phaseCard = true;
    } else if (actionType === 'phaseCardCharge') {
      usedActionsThisTurn.phaseCardCharge = true;
      chargeUsedThisTurn = true;  // モンスターチャージと共有
    } else if (actionType === 'magic') {
      usedActionsThisTurn.magic = true;  // 1ターン1回に制限（戦略で制御）
    } else if (actualAction.type === 'CHARGE_CARD') {
      chargeUsedThisTurn = true;
    }

    newState = applyAction(newState, actualAction);
    mainPhaseActions++;
  }
  newState = applyAction(newState, actions.nextPhase());

  // バトルフェイズ
  if (debugLog) {
    debugLog.setContext(state.turn, state.currentPlayer, 'battle');
  }
  // バトルフェイズ開始トリガー発火（両方発火：汎用 + 自分専用）
  newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_BATTLE_PHASE_START, {});
  newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_BATTLE_PHASE_START_SELF, {});

  // ★ BATTLE_START チェーンポイント（刹那詠唱）
  if (setsunaStats) {
    newState = processSetsunaChainPoint(
      newState,
      CHAIN_POINTS.BATTLE_START,
      { type: 'battleStart' },
      opponentStrategy,
      setsunaStats,
      enableSetsuna,
      debugLog
    );
  }

  let battlePhaseActions = 0;
  const maxBattleActions = 10; // 無限ループ防止
  const attackedMonsters = new Set<number>();

  while (battlePhaseActions < maxBattleActions && newState.gameStatus !== GAME_STATUS.GAME_OVER) {
    const result = getAIBattlePhaseAction(newState, strategy, attackedMonsters, debugLog);
    if (!result) break;

    // ★ ATTACK_DECLARATION チェーンポイント（刹那詠唱）
    if (setsunaStats) {
      const attackerField = newState.currentPlayer === 1 ? newState.p1.field : newState.p2.field;
      const attacker = attackerField[result.attackerIndex];
      const chainContext: ChainContext = { type: 'attackDeclaration' };
      if (attacker) {
        chainContext.attacker = attacker;
      }
      newState = processSetsunaChainPoint(
        newState,
        CHAIN_POINTS.ATTACK_DECLARATION,
        chainContext,
        opponentStrategy,
        setsunaStats,
        enableSetsuna,
        debugLog
      );
    }

    attackedMonsters.add(result.attackerIndex);
    newState = applyAction(newState, result.action);
    battlePhaseActions++;
  }
  newState = applyAction(newState, actions.nextPhase());

  // エンドフェイズ
  if (debugLog) {
    debugLog.setContext(state.turn, state.currentPlayer, 'end');
  }
  newState = applyAction(newState, actions.processPhase(PHASES.END));
  // エンドフェイズトリガー発火
  newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_END_PHASE_SELF, {});
  newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_END_PHASE, {});
  // 相手エンドフェイズトリガー発火（相手のカードが対象）
  newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_OPPONENT_END_PHASE, {});
  newState = applyAction(newState, actions.nextPhase()); // ターン終了

  return newState;
}

/**
 * ゲーム全体をシミュレート
 */
export function simulateGame(config: SimulationConfig): SimulationResult {
  const {
    deck1,
    deck2,
    firstPlayer,
    maxTurns = SIMULATION_CONFIG.MAX_TURNS,
    p1Difficulty = 'normal',
    p2Difficulty = 'normal',
    summoningSickness = true, // 召喚酔い（デフォルトON）
    secondPlayerBonusSP = true, // 後攻ボーナスSP（デフォルトON）
    piercingDamage = true, // 貫通ダメージ（デフォルトON）
    loneWarriorRule = true, // 孤軍奮闘（デフォルトON）
    enableSetsuna = true, // 刹那詠唱（デフォルトON）
    initialLifePoints, // 初期LP（省略時はデフォルト6000）
  } = config;

  // ストラテジーを取得
  const p1Strategy = getStrategy(p1Difficulty);
  const p2Strategy = getStrategy(p2Difficulty);

  let state = createInitialState({ deck1, deck2, firstPlayer, summoningSickness, secondPlayerBonusSP, piercingDamage, loneWarriorRule, initialLifePoints });
  const actualFirstPlayer = state.currentPlayer; // 実際の先攻プレイヤーを記録
  const startTime = Date.now();

  // 刹那詠唱統計を初期化
  const setsunaStats = createSetsunaStats();

  // ゲーム実行（デバッグログなし - 高速）
  while (state.gameStatus !== GAME_STATUS.GAME_OVER && state.turn <= maxTurns) {
    state = executeTurn(state, p1Strategy, p2Strategy, null, setsunaStats, enableSetsuna);

    // P1のターンが終わったらP2のターン
    if (state.currentPlayer === 1 && state.gameStatus !== GAME_STATUS.GAME_OVER) {
      state = executeTurn(state, p1Strategy, p2Strategy, null, setsunaStats, enableSetsuna);
    }
  }

  const endTime = Date.now();

  // 最大ターン到達時の判定
  let winner: PlayerNumber | 0 | null = state.winner;
  if (!winner && state.turn > maxTurns) {
    // ライフが多い方が勝ち
    if (state.p1.life > state.p2.life) {
      winner = 1;
    } else if (state.p2.life > state.p1.life) {
      winner = 2;
    } else {
      winner = 0; // 引き分け
    }
  }

  return {
    winner,
    firstPlayer: actualFirstPlayer, // 先攻プレイヤー
    turns: state.turn,
    p1Life: state.p1.life,
    p2Life: state.p2.life,
    p1Graveyard: state.p1.graveyard.length,
    p2Graveyard: state.p2.graveyard.length,
    // 追加統計データ
    p1FieldCount: state.p1.field.filter(m => m !== null).length,
    p2FieldCount: state.p2.field.filter(m => m !== null).length,
    p1HandCount: state.p1.hand.length,
    p2HandCount: state.p2.hand.length,
    p1ActiveSP: state.p1.activeSP,
    p2ActiveSP: state.p2.activeSP,
    p1RestedSP: state.p1.restedSP,
    p2RestedSP: state.p2.restedSP,
    duration: endTime - startTime,
    logs: state.logs,
    // カード統計用に最終状態を含める
    finalState: state,
    // 刹那詠唱統計
    setsunaStats,
  };
}

/**
 * デバッグモードでゲームをシミュレート（AI思考ログ付き）
 */
export function simulateGameDebug(config: SimulationConfig): SimulationDebugResult {
  const {
    deck1,
    deck2,
    firstPlayer,
    maxTurns = SIMULATION_CONFIG.MAX_TURNS,
    p1Difficulty = 'normal',
    p2Difficulty = 'normal',
    summoningSickness = true, // 召喚酔い（デフォルトON）
    secondPlayerBonusSP = true, // 後攻ボーナスSP（デフォルトON）
    piercingDamage = true, // 貫通ダメージ（デフォルトON）
    loneWarriorRule = true, // 孤軍奮闘（デフォルトON）
    enableSetsuna = true, // 刹那詠唱（デフォルトON）
    initialLifePoints, // 初期LP（省略時はデフォルト6000）
  } = config;

  // ストラテジーを取得
  const p1Strategy = getStrategy(p1Difficulty);
  const p2Strategy = getStrategy(p2Difficulty);

  // デバッグログを作成
  const debugLog = new AIDecisionLog();

  // 刹那詠唱統計を初期化
  const setsunaStats = createSetsunaStats();

  let state = createInitialState({ deck1, deck2, firstPlayer, summoningSickness, secondPlayerBonusSP, piercingDamage, loneWarriorRule, initialLifePoints });
  const actualFirstPlayer = state.currentPlayer; // 実際の先攻プレイヤーを記録
  const startTime = Date.now();

  // ゲーム実行（デバッグログ有効）
  while (state.gameStatus !== GAME_STATUS.GAME_OVER && state.turn <= maxTurns) {
    state = executeTurn(state, p1Strategy, p2Strategy, debugLog, setsunaStats, enableSetsuna);

    // P1のターンが終わったらP2のターン
    if (state.currentPlayer === 1 && state.gameStatus !== GAME_STATUS.GAME_OVER) {
      state = executeTurn(state, p1Strategy, p2Strategy, debugLog, setsunaStats, enableSetsuna);
    }
  }

  const endTime = Date.now();

  // 最大ターン到達時の判定
  let winner: PlayerNumber | 0 | null = state.winner;
  if (!winner && state.turn > maxTurns) {
    if (state.p1.life > state.p2.life) {
      winner = 1;
    } else if (state.p2.life > state.p1.life) {
      winner = 2;
    } else {
      winner = 0;
    }
  }

  return {
    winner,
    firstPlayer: actualFirstPlayer, // 先攻プレイヤー
    turns: state.turn,
    p1Life: state.p1.life,
    p2Life: state.p2.life,
    p1Graveyard: state.p1.graveyard.length,
    p2Graveyard: state.p2.graveyard.length,
    // 追加統計データ
    p1FieldCount: state.p1.field.filter(m => m !== null).length,
    p2FieldCount: state.p2.field.filter(m => m !== null).length,
    p1HandCount: state.p1.hand.length,
    p2HandCount: state.p2.hand.length,
    p1ActiveSP: state.p1.activeSP,
    p2ActiveSP: state.p2.activeSP,
    p1RestedSP: state.p1.restedSP,
    p2RestedSP: state.p2.restedSP,
    duration: endTime - startTime,
    logs: state.logs,
    // デバッグ情報
    aiDecisions: debugLog.decisions,
    aiDecisionsSummary: debugLog.getSummary(),
    aiDecisionsFormatted: debugLog.formatForDisplay(),
    // カテゴリ別取得
    getMagicDecisions: () => debugLog.getByCategory('magic'),
    getSkillDecisions: () => debugLog.getByCategory('skill'),
    getSummonDecisions: () => debugLog.getByCategory('summon'),
    getAttackDecisions: () => debugLog.getByCategory('attack'),
    getSetsunaDecisions: () => debugLog.getByCategory('setsuna'),
    // 刹那詠唱統計
    setsunaStats,
  };
}

/**
 * AIDecisionLogクラスをエクスポート（カスタム分析用）
 */
export { AIDecisionLog };

// ========================================
// カード統計トラッカー
// ========================================

/** プレイ履歴エントリの型（PlayedCardEntryに相当） */
interface PlayedCardHistoryEntry {
  id: string;
  name: string;
  attribute: string;
  cost: number;
  type: string;
}

/**
 * カード別統計を追跡するクラス
 * プレイ回数、プレイ時勝率を集計
 */
class CardStatsTracker {
  p1Stats: Record<string, CardStatEntry>;
  p2Stats: Record<string, CardStatEntry>;

  constructor() {
    this.p1Stats = {};
    this.p2Stats = {};
  }

  /**
   * ゲーム結果からカード統計を記録
   */
  recordGame(state: GameState, winner: PlayerNumber | 0 | null): void {
    const p1PlayedCards = state.playedCardsHistory?.p1 || [];
    const p2PlayedCards = state.playedCardsHistory?.p2 || [];

    for (const card of p1PlayedCards) {
      this._recordCardPlay(this.p1Stats, card, winner === 1);
    }

    for (const card of p2PlayedCards) {
      this._recordCardPlay(this.p2Stats, card, winner === 2);
    }
  }

  /**
   * カードプレイを統計に記録
   */
  _recordCardPlay(stats: Record<string, CardStatEntry>, card: PlayedCardHistoryEntry, isWin: boolean): void {
    if (!stats[card.id]) {
      stats[card.id] = {
        id: card.id,
        name: card.name,
        attribute: card.attribute,
        cost: card.cost,
        type: card.type,
        plays: 0,
        wins: 0,
        games: 0,
      };
    }
    stats[card.id].plays++;
    if (isWin) {
      stats[card.id].wins++;
    }
  }

  /**
   * ゲーム数をカウント（同一ゲームでの複数プレイを1ゲームとしてカウント）
   */
  recordGameCount(state: GameState, winner: PlayerNumber | 0 | null): void {
    const p1CardIds = new Set<string>();
    const p2CardIds = new Set<string>();

    const p1History = state.playedCardsHistory?.p1 || [];
    for (const card of p1History) {
      p1CardIds.add(card.id);
    }

    const p2History = state.playedCardsHistory?.p2 || [];
    for (const card of p2History) {
      p2CardIds.add(card.id);
    }

    for (const cardId of p1CardIds) {
      if (this.p1Stats[cardId]) {
        this.p1Stats[cardId].games++;
      }
    }
    for (const cardId of p2CardIds) {
      if (this.p2Stats[cardId]) {
        this.p2Stats[cardId].games++;
      }
    }
  }

  /**
   * 別のトラッカーの統計をマージ
   */
  merge(other: CardStatsTracker): void {
    this._mergeStats(this.p1Stats, other.p1Stats);
    this._mergeStats(this.p2Stats, other.p2Stats);
  }

  _mergeStats(target: Record<string, CardStatEntry>, source: Record<string, CardStatEntry>): void {
    for (const [cardId, stats] of Object.entries(source)) {
      if (!target[cardId]) {
        target[cardId] = { ...stats };
      } else {
        target[cardId].plays += stats.plays;
        target[cardId].wins += stats.wins;
        target[cardId].games += stats.games;
      }
    }
  }

  /**
   * 統計サマリーを取得
   */
  getSummary(): CardStatsSummary {
    return {
      p1: this._formatStats(this.p1Stats),
      p2: this._formatStats(this.p2Stats),
    };
  }

  _formatStats(stats: Record<string, CardStatEntry>): CardStatWithRates[] {
    return Object.values(stats)
      .map(s => ({
        ...s,
        winRate: s.games > 0 ? s.wins / s.games : 0,
        playRate: 0,
      }))
      .sort((a, b) => b.plays - a.plays);
  }
}

/**
 * カード統計を含むシミュレーション結果を返す
 */
export function simulateGameWithStats(config: SimulationConfig): SimulationResult {
  const result = simulateGame(config);

  // カード統計を計算するため、状態を取得する必要がある
  // simulateGame内部で最終状態を返すように拡張が必要
  // 現時点では簡易実装として、simulateMultiple側で統計を取る

  return result;
}

/**
 * 複数回シミュレートして統計を取得
 */
export function simulateMultiple(config: MultipleSimulationConfig): MultipleSimulationResult {
  const {
    deck1,
    deck2,
    count = 10,
    p1Difficulty = 'normal',
    p2Difficulty = 'normal',
    maxTurns = SIMULATION_CONFIG.MAX_TURNS,
    summoningSickness = true, // 召喚酔い（デフォルトON）
    secondPlayerBonusSP = true, // 後攻ボーナスSP（デフォルトON）
    piercingDamage = true, // 貫通ダメージ（デフォルトON）
    loneWarriorRule = true, // 孤軍奮闘（デフォルトON）
    enableSetsuna = true, // 刹那詠唱（デフォルトON）
    initialLifePoints, // 初期LP（省略時はデフォルト6000）
  } = config;

  // カード統計トラッカー
  const cardStatsTracker = new CardStatsTracker();

  const results: MultipleSimulationResult = {
    p1Wins: 0,
    p2Wins: 0,
    draws: 0,
    totalTurns: 0,
    totalDuration: 0,
    games: [],
    // 先攻/後攻統計
    firstPlayerStats: {
      p1First: 0,      // P1が先攻だった回数
      p2First: 0,      // P2が先攻だった回数
      firstWins: 0,    // 先攻が勝った回数
      secondWins: 0,   // 後攻が勝った回数
      // P1の先攻/後攻別勝利数
      p1WinsWhenFirst: 0,   // P1先攻時のP1勝利
      p1WinsWhenSecond: 0,  // P1後攻時のP1勝利
      // P2の先攻/後攻別勝利数
      p2WinsWhenFirst: 0,   // P2先攻時のP2勝利
      p2WinsWhenSecond: 0,  // P2後攻時のP2勝利
    },
    // ターン分布
    turnDistribution: {},
    // ライフ統計
    lifeStats: {
      winnerLifeTotal: 0,
      loserLifeTotal: 0,
      winnerLifeList: [],
      decidedGamesCount: 0,
    },
    // SP統計
    spStats: {
      p1TotalActiveSP: 0,
      p1TotalRestedSP: 0,
      p2TotalActiveSP: 0,
      p2TotalRestedSP: 0,
      p1TotalCardsUsed: 0,  // 墓地+フィールド枚数（使用カード数の目安）
      p2TotalCardsUsed: 0,
    },
    // 刹那詠唱統計
    setsunaStats: {
      p1: { opportunities: 0, activations: 0 },
      p2: { opportunities: 0, activations: 0 },
      cardStats: {},
    },
  };

  for (let i = 0; i < count; i++) {
    // デッキをシャッフル
    const shuffledDeck1 = shuffleArray([...deck1]);
    const shuffledDeck2 = shuffleArray([...deck2]);

    const result = simulateGame({
      deck1: shuffledDeck1,
      deck2: shuffledDeck2,
      p1Difficulty,
      p2Difficulty,
      maxTurns,
      summoningSickness,
      secondPlayerBonusSP,
      piercingDamage,
      loneWarriorRule,
      enableSetsuna,
      initialLifePoints,
    });

    // 刹那詠唱統計を集計
    if (result.setsunaStats) {
      // 全体統計
      results.setsunaStats.p1.opportunities += result.setsunaStats.p1.opportunities;
      results.setsunaStats.p1.activations += result.setsunaStats.p1.activations;
      results.setsunaStats.p2.opportunities += result.setsunaStats.p2.opportunities;
      results.setsunaStats.p2.activations += result.setsunaStats.p2.activations;

      // カード別統計
      for (const [cardId, cardStat] of Object.entries(result.setsunaStats.cardStats || {})) {
        if (!results.setsunaStats.cardStats[cardId]) {
          results.setsunaStats.cardStats[cardId] = {
            cardId: cardStat.cardId,
            name: cardStat.name,
            attribute: cardStat.attribute,
            cost: cardStat.cost,
            p1Opportunities: 0,
            p1Activations: 0,
            p2Opportunities: 0,
            p2Activations: 0,
          };
        }
        results.setsunaStats.cardStats[cardId].p1Opportunities += cardStat.p1Opportunities || 0;
        results.setsunaStats.cardStats[cardId].p1Activations += cardStat.p1Activations || 0;
        results.setsunaStats.cardStats[cardId].p2Opportunities += cardStat.p2Opportunities || 0;
        results.setsunaStats.cardStats[cardId].p2Activations += cardStat.p2Activations || 0;
      }
    }

    if (result.winner === 1) {
      results.p1Wins++;
    } else if (result.winner === 2) {
      results.p2Wins++;
    } else {
      results.draws++;
    }

    // カード統計を記録
    if (result.finalState) {
      cardStatsTracker.recordGame(result.finalState, result.winner);
      cardStatsTracker.recordGameCount(result.finalState, result.winner);
    }

    // 先攻/後攻統計を集計
    if (result.firstPlayer === 1) {
      results.firstPlayerStats.p1First++;
    } else {
      results.firstPlayerStats.p2First++;
    }

    if (result.winner !== 0) { // 引き分け以外
      if (result.winner === result.firstPlayer) {
        results.firstPlayerStats.firstWins++;
      } else {
        results.firstPlayerStats.secondWins++;
      }

      // P1/P2それぞれの先攻/後攻別勝率を集計
      if (result.winner === 1) {
        // P1勝利
        if (result.firstPlayer === 1) {
          results.firstPlayerStats.p1WinsWhenFirst++;
        } else {
          results.firstPlayerStats.p1WinsWhenSecond++;
        }
      } else if (result.winner === 2) {
        // P2勝利
        if (result.firstPlayer === 2) {
          results.firstPlayerStats.p2WinsWhenFirst++;
        } else {
          results.firstPlayerStats.p2WinsWhenSecond++;
        }
      }
    }

    results.totalTurns += result.turns;
    results.totalDuration += result.duration;
    results.games.push(result);

    // ターン分布を集計
    const turn = result.turns;
    results.turnDistribution[turn] = (results.turnDistribution[turn] || 0) + 1;

    // ライフ統計を集計（引き分け以外）
    if (result.winner !== 0) {
      const winnerLife = result.winner === 1 ? result.p1Life : result.p2Life;
      const loserLife = result.winner === 1 ? result.p2Life : result.p1Life;
      results.lifeStats.winnerLifeTotal += winnerLife;
      results.lifeStats.loserLifeTotal += loserLife;
      results.lifeStats.winnerLifeList.push(winnerLife);
      results.lifeStats.decidedGamesCount++;
    }

    // SP統計を集計
    results.spStats.p1TotalActiveSP += result.p1ActiveSP || 0;
    results.spStats.p1TotalRestedSP += result.p1RestedSP || 0;
    results.spStats.p2TotalActiveSP += result.p2ActiveSP || 0;
    results.spStats.p2TotalRestedSP += result.p2RestedSP || 0;
    results.spStats.p1TotalCardsUsed += (result.p1Graveyard || 0) + (result.p1FieldCount || 0);
    results.spStats.p2TotalCardsUsed += (result.p2Graveyard || 0) + (result.p2FieldCount || 0);
  }

  results.avgTurns = results.totalTurns / count;
  results.avgDuration = results.totalDuration / count;
  results.p1WinRate = results.p1Wins / count;
  results.p2WinRate = results.p2Wins / count;

  // 先攻/後攻勝率を計算
  const decidedGames = results.firstPlayerStats.firstWins + results.firstPlayerStats.secondWins;
  results.firstPlayerStats.firstWinRate = decidedGames > 0
    ? results.firstPlayerStats.firstWins / decidedGames
    : 0;
  results.firstPlayerStats.secondWinRate = decidedGames > 0
    ? results.firstPlayerStats.secondWins / decidedGames
    : 0;

  // P1の先攻/後攻別勝率を計算
  const p1FirstGames = results.firstPlayerStats.p1First;
  const p2FirstGames = results.firstPlayerStats.p2First;
  results.firstPlayerStats.p1WinRateWhenFirst = p1FirstGames > 0
    ? results.firstPlayerStats.p1WinsWhenFirst / p1FirstGames
    : 0;
  results.firstPlayerStats.p1WinRateWhenSecond = p2FirstGames > 0
    ? results.firstPlayerStats.p1WinsWhenSecond / p2FirstGames
    : 0;

  // P2の先攻/後攻別勝率を計算
  results.firstPlayerStats.p2WinRateWhenFirst = p2FirstGames > 0
    ? results.firstPlayerStats.p2WinsWhenFirst / p2FirstGames
    : 0;
  results.firstPlayerStats.p2WinRateWhenSecond = p1FirstGames > 0
    ? results.firstPlayerStats.p2WinsWhenSecond / p1FirstGames
    : 0;

  // ライフ統計の平均計算
  const lifeDecidedGames = results.lifeStats.decidedGamesCount;
  if (lifeDecidedGames > 0) {
    results.lifeStats.avgWinnerLife = results.lifeStats.winnerLifeTotal / lifeDecidedGames;
    results.lifeStats.avgLoserLife = results.lifeStats.loserLifeTotal / lifeDecidedGames;
    // ライフ分布（0-1000, 1000-2000, ... のヒストグラム）
    const lifeBuckets: Record<number, number> = {};
    results.lifeStats.winnerLifeList.forEach(life => {
      const bucket = Math.floor(life / 1000) * 1000;
      lifeBuckets[bucket] = (lifeBuckets[bucket] || 0) + 1;
    });
    results.lifeStats.winnerLifeDistribution = lifeBuckets;
  }

  // SP統計の平均計算
  results.spStats.avgP1ActiveSP = results.spStats.p1TotalActiveSP / count;
  results.spStats.avgP1RestedSP = results.spStats.p1TotalRestedSP / count;
  results.spStats.avgP2ActiveSP = results.spStats.p2TotalActiveSP / count;
  results.spStats.avgP2RestedSP = results.spStats.p2TotalRestedSP / count;
  results.spStats.avgP1CardsUsed = results.spStats.p1TotalCardsUsed / count;
  results.spStats.avgP2CardsUsed = results.spStats.p2TotalCardsUsed / count;
  // SP効率 = 使用カード数 / (消費SP = restedSP)　※大きいほど効率が良い
  results.spStats.p1Efficiency = results.spStats.p1TotalRestedSP > 0
    ? results.spStats.p1TotalCardsUsed / results.spStats.p1TotalRestedSP
    : 0;
  results.spStats.p2Efficiency = results.spStats.p2TotalRestedSP > 0
    ? results.spStats.p2TotalCardsUsed / results.spStats.p2TotalRestedSP
    : 0;

  // カード統計を結果に追加
  const cardStatsSummary = cardStatsTracker.getSummary();
  // playRateを計算（総ゲーム数に対する出場率）
  cardStatsSummary.p1.forEach(card => {
    card.playRate = count > 0 ? card.games / count : 0;
  });
  cardStatsSummary.p2.forEach(card => {
    card.playRate = count > 0 ? card.games / count : 0;
  });
  results.cardStats = cardStatsSummary;
  results.totalGames = count;

  return results;
}

/**
 * オッズを計算
 */
export function calculateOdds(stats: MultipleSimulationResult): OddsResult {
  const total = stats.p1Wins + stats.p2Wins + stats.draws;

  // 勝率からオッズを計算（簡易版）
  // オッズ = 1 / 勝率
  const p1WinRate = stats.p1Wins / total || 0.01;
  const p2WinRate = stats.p2Wins / total || 0.01;

  return {
    p1Odds: Math.max(1.01, Math.round((1 / p1WinRate) * 100) / 100),
    p2Odds: Math.max(1.01, Math.round((1 / p2WinRate) * 100) / 100),
    p1WinRate: Math.round(p1WinRate * 1000) / 10,
    p2WinRate: Math.round(p2WinRate * 1000) / 10,
  };
}

// ========================================
// ヘルパー
// ========================================

/**
 * 配列をシャッフル（Fisher-Yates）
 */
function shuffleArray<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}
